import { useEffect, useState, RefObject } from 'react';

interface UseTrimmerDragOptions {
    containerRef: RefObject<HTMLDivElement | null>;
    containerWidth: number;
    duration: number;
    trimStart: number;
    trimEnd: number;
    getTimeFromPosition: (position: number) => number;
    onTrimChange: (start: number, end: number) => void;
}

interface UseTrimmerDragReturn {
    isDragging: 'left' | 'right' | 'middle' | null;
    handleLeftMouseDown: (e: React.MouseEvent) => void;
    handleRightMouseDown: (e: React.MouseEvent) => void;
    handleMiddleMouseDown: (e: React.MouseEvent) => void;
}

export function useTrimmerDrag({
    containerRef,
    containerWidth,
    duration,
    trimStart,
    trimEnd,
    getTimeFromPosition,
    onTrimChange,
}: UseTrimmerDragOptions): UseTrimmerDragReturn {
    const [isDragging, setIsDragging] = useState<
        'left' | 'right' | 'middle' | null
    >(null);
    const [dragStartX, setDragStartX] = useState<number>(0);
    const [dragStartTrimStart, setDragStartTrimStart] = useState<number>(0);
    const [dragStartTrimEnd, setDragStartTrimEnd] = useState<number>(0);

    const handleLeftMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging('left');
    };

    const handleRightMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging('right');
    };

    const handleMiddleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging('middle');
        setDragStartX(e.clientX);
        setDragStartTrimStart(trimStart);
        setDragStartTrimEnd(trimEnd);
    };

    useEffect(() => {
        if (!isDragging || !containerRef.current) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const clampedX = Math.max(0, Math.min(containerWidth, x));
            const time = getTimeFromPosition(clampedX);

            if (isDragging === 'left') {
                const newStart = Math.max(0, Math.min(time, trimEnd - 0.1));
                onTrimChange(newStart, trimEnd);
            } else if (isDragging === 'right') {
                const newEnd = Math.max(
                    trimStart + 0.1,
                    Math.min(duration, time),
                );
                onTrimChange(trimStart, newEnd);
            } else if (isDragging === 'middle') {
                const deltaX = e.clientX - dragStartX;
                const deltaTime =
                    getTimeFromPosition(deltaX) - getTimeFromPosition(0);

                const selectionDuration = dragStartTrimEnd - dragStartTrimStart;
                let newStart = dragStartTrimStart + deltaTime;
                let newEnd = dragStartTrimEnd + deltaTime;

                if (newStart < 0) {
                    newStart = 0;
                    newEnd = selectionDuration;
                } else if (newEnd > duration) {
                    newEnd = duration;
                    newStart = duration - selectionDuration;
                }

                onTrimChange(newStart, newEnd);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [
        isDragging,
        containerWidth,
        duration,
        trimStart,
        trimEnd,
        onTrimChange,
        dragStartX,
        dragStartTrimStart,
        dragStartTrimEnd,
        getTimeFromPosition,
        containerRef,
    ]);

    return {
        isDragging,
        handleLeftMouseDown,
        handleRightMouseDown,
        handleMiddleMouseDown,
    };
}
